import { useMutation, useQuery } from "@apollo/client";
import { Formik } from "formik";
import { useState } from "react";
import { useToasts } from "react-toast-notifications";
import { Prompt } from "react-router";
import Button from "../../Button/button.component";
import Input from "../../Input/input.component";
import MsgBoxSelect from "../../MsgBoxSelect/msg-box-select.component";
import { JobAdderSettings } from "../providers/jobadder";
import { ReapitJournalTypeSelect } from "../providers/reapit";
import {
	crmIntegrationMap,
	crmIntegrationOptions,
	crmFrequencyMap,
	crmFrequencyOptions,
	EMAIL_REGEX,
} from "../helper";
import {
	GET_INBOX_CONFIG,
	UPDATE_INBOX_CONFIG,
	GET_ORG_ID,
	UPDATE_VINCERE_CONNECTED,
} from "./query";

/// inbox configuration of automation settings. jobadder added using individual component, future integrations should follow this pattern.
const EditInboxSettings = ({ rootStore, config, id }) => {
	const { addToast } = useToasts();
	const [dirty, setDirty] = useState(false);
	const [emailError, setEmailError] = useState("");
	const [vincereClientId, setVincereClientID] = useState("");
	const [vincereApiKey, setVincereApiKey] = useState("");
	const [vincereTenant, setVincereTenant] = useState("");
	const [vincereTenantError, setVincereTenantError] = useState("");
	const [eployTenantError, setEployTenantError] = useState("");
	const orgId = useQuery(GET_ORG_ID);
	const [
		updateVincereConnected,
		{ error: vincereError, loading: vincereLoading },
	] = useMutation(UPDATE_VINCERE_CONNECTED);

	function handleVincereOAuth() {
		if (vincereTenant.includes(".")) {
			setVincereTenantError(
				"Enter only your Vincere tenant name (for example: company), not a full URL."
			);
			return;
		}

		setVincereTenantError("");
		setDirty(true);
		const jsonState = encodeURIComponent(
			JSON.stringify({
				orgId: orgId.data.msgbox_Organisation[0].OrganisationId,
				inboxId: id,
				clientId: vincereClientId,
				apiKey: vincereApiKey,
				tenant: vincereTenant,
			})
		);

		const uri =
			`https://id.vincere.io/oauth2/authorize?client_id=` +
			vincereClientId +
			"&state=" +
			jsonState +
			`&redirect_uri=${process.env.REACT_APP_VINCERE_REDIRECT_URI}` +
			"&response_type=code";

		window.open(uri, "_blank");
	}

	const [updateInboxConfig, { loading }] = useMutation(UPDATE_INBOX_CONFIG, {
		refetchQueries: [
			{
				query: GET_INBOX_CONFIG,
				variables: {
					inboxId: id,
				},
			},
		],
		onCompleted: () => {
			addToast("Inbox settings updated successfully", {
				appearance: "success",
				autoDismiss: true,
			});
		},
	});

	const handleVincereConnected = async (boolConnected) => {
		setDirty(true);
		try {
			updateVincereConnected({
				variables: {
					inboxId: config.InboxId,
					IsVincereConnected: boolConnected,
				},
			});
		} catch (e) {
			console.log(e);
		}

		if (boolConnected === false) {
			setDirty(true);
		}
		addToast("Connection updated successfully", {
			appearance: "success",
			autoDismiss: true,
		});
	};

	const handleSubmit = async (values) => {
		try {
			const {
				crmEmail,
				crmIntegration,
				reapitCustomerId,
				reapitNegotiatorId,
				reapitOfficeId,
				reapitJournalType,
				bullhornClientId,
				bullhornUsername,
				bullhornPassword,
				bullhornClientSecret,
				crmFrequency,
				altoAgencyRef,
				eployTenant,
				eployUserId,
				eployClientId,
				eploySecret,
			} = values;

			if (
				(crmIntegration.value === "Rex" ||
					crmIntegration.value === "Hubspot" ||
					crmIntegration.value === "Salesforce" ||
					crmIntegration.value === "Generic Email Forward" ||
					crmIntegration.value === "Email Audit") &&
				!EMAIL_REGEX.test(crmEmail.value)
			) {
				return setEmailError("Invalid Email");
			} else {
				setEmailError("");
			}

			if (
				crmIntegration.value === "Eploy" &&
				eployTenant.value &&
				eployTenant.value.includes(".")
			) {
				return setEployTenantError(
					"Enter only your Eploy tenant name (for example: company), not a full URL."
				);
			} else {
				setEployTenantError("");
			}

			await updateInboxConfig({
				variables: {
					inboxId: id,
					crmEmail: crmEmail.value ? crmEmail.value : null,
					crmIntegration: crmIntegration.value,
					reapitCustomerId: reapitCustomerId.value
						? reapitCustomerId.value
						: null,
					reapitNegotiatorId: reapitNegotiatorId.value
						? reapitNegotiatorId.value
						: null,
					reapitOfficeId: reapitOfficeId.value
						? reapitOfficeId.value
						: null,
					reapitJournalType: reapitJournalType.value
						? reapitJournalType.value
						: null,
					bullhornClientId: bullhornClientId.value
						? bullhornClientId.value
						: null,
					bullhornUsername: bullhornUsername.value
						? bullhornUsername.value
						: null,
					bullhornPassword: bullhornPassword.value
						? bullhornPassword.value
						: null,
					bullhornClientSecret: bullhornClientSecret.value
						? bullhornClientSecret.value
						: null,
					altoAgencyRef: altoAgencyRef.value
						? altoAgencyRef.value
						: null,
					crmFrequency: crmFrequency.value,
					eployTenant: eployTenant.value
						? eployTenant.value
						: null,
					eployUserId: eployUserId.value
						? eployUserId.value
						: null,
					eployClientId: eployClientId.value
						? eployClientId.value
						: null,
					eploySecret: eploySecret.value
						? eploySecret.value
						: config.EployClientSecret || null,
				},
			});
			setDirty(false);
		} catch (e) {
			addToast("Something went wrong", {
				appearance: "error",
				autoDismiss: true,
			});
		}
	};
	return (
		<>
			<Formik
				initialValues={{
					crmEmail: {
						value: config.CRMEmail != null ? config.CRMEmail : "",
					},
					crmIntegration: {
						label: crmIntegrationMap[config.CRMIntegration]
							? crmIntegrationMap[config.CRMIntegration]
							: "Vincere",
						value: config.CRMIntegration,
					},
					reapitCustomerId: {
						value:
							config.ReapitCustomerId != null
								? config.ReapitCustomerId
								: "",
					},
					reapitNegotiatorId: {
						value:
							config.ReapitNegotiatorId != null
								? config.ReapitNegotiatorId
								: "",
					},
					reapitOfficeId: {
						value:
							config.ReapitOfficeId != null
								? config.ReapitOfficeId
								: "",
					},
					reapitJournalType: {
						value:
							config.ReapitJournalType != null
								? config.ReapitJournalType
								: null,
						label:
							config.ReapitJournalType != null
								? config.ReapitJournalType
								: "",
					},
					bullhornClientId: {
						value:
							config.BullhornClientId != null
								? config.BullhornClientId
								: "",
					},
					bullhornUsername: {
						value:
							config.BullhornUsername != null
								? config.BullhornUsername
								: "",
					},
					bullhornPassword: {
						value:
							config.BullhornPassword != null
								? config.BullhornPassword
								: "",
					},
					bullhornClientSecret: {
						value:
							config.BullhornClientSecret != null
								? config.BullhornClientSecret
								: "",
					},
					altoAgencyRef: {
						value:
							config.AltoAgencyRef != null
								? config.AltoAgencyRef
								: "",
					},
					crmFrequency: {
						label: crmFrequencyMap[config.CRMFrequency],
						value: config.CRMFrequency,
					},
					vincereClientId: {
						label: "Vincere Client Id",
						value: "",
					},
					vincereApiKey: {
						label: "Vincere Api Key",
						value: "",
					},
					vincereTenant: {
						label: "Vincere Tenant",
						value: "",
					},
					eployTenant: {
						value:
							config.EployTenant != null
								? config.EployTenant
								: "",
					},
					eployUserId: {
						value:
							config.EployUserId != null
								? config.EployUserId
								: "",
					},
					eployClientId: {
						value:
							config.EployClientId != null
								? config.EployClientId
								: "",
					},
					eploySecret: {
						value: "",
					},
				}}
				enableReinitialize={true}
				onSubmit={handleSubmit}
			>
				{({ handleSubmit, setFieldValue, values }) => (
					<form
						id={`automation-inbox-settings-${id}`}
						onSubmit={handleSubmit}
					>
						<MsgBoxSelect
							options={crmIntegrationOptions}
							onChange={(options) => {
								setFieldValue("crmIntegration", options);
								setDirty(true);
							}}
							name="crmIntegration"
							label="CRM Integration"
							value={values.crmIntegration}
						/>

						{values.crmIntegration.value === "Rex" ||
						values.crmIntegration.value === "Hubspot" ||
						values.crmIntegration.value === "Salesforce" ||
						values.crmIntegration.value ===
							"Generic Email Forward" ||
						values.crmIntegration.value === "Email Audit" ? (
							<Input
								onChange={(options) => {
									setFieldValue("crmEmail", options.target);
									setDirty(true);
								}}
								name="crmEmail"
								error={emailError}
								label="Email for CRM Integration"
								value={values.crmEmail.value}
							/>
						) : (
							<></>
						)}
						{values.crmIntegration.value !== "None" &&
						values.crmIntegration.value ? (
							<MsgBoxSelect
								toolTipText="Explain here"
								options={crmFrequencyOptions}
								onChange={(options) => {
									setFieldValue("crmFrequency", options);
									setDirty(true);
								}}
								name="crmFrequency"
								label="Frequency"
								value={values.crmFrequency}
							/>
						) : (
							<></>
						)}
						{values.crmIntegration.value === "Reapit" ? (
							<>
								<Input
									onChange={(options) => {
										setFieldValue(
											"reapitCustomerId",
											options.target
										);
										setDirty(true);
									}}
									name="reapitCustomerId"
									label="Reapit Customer Id"
									value={values.reapitCustomerId.value}
								/>
								<Input
									onChange={(options) => {
										setFieldValue(
											"reapitNegotiatorId",
											options.target
										);
										setDirty(true);
									}}
									name="reapitNegotiatorId"
									label="Reapit Negotiator Id"
									value={values.reapitNegotiatorId.value}
								/>
								<Input
									onChange={(options) => {
										setFieldValue(
											"reapitOfficeId",
											options.target
										);
										setDirty(true);
									}}
									name="reapitOfficeId"
									label="Reapit Office Id"
									value={values.reapitOfficeId.value}
								/>
								<ReapitJournalTypeSelect
									customerId={values.reapitCustomerId.value}
									value={values.reapitJournalType.value}
									onChange={(option) => {
										setFieldValue(
											"reapitJournalType",
											option || { value: null, label: "" }
										);
										setDirty(true);
									}}
								/>
							</>
						) : (
							<></>
						)}

						{values.crmIntegration.value === "Bullhorn" ? (
							<>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										console.log("options:", options.target);
										setFieldValue(
											"bullhornClientId",
											options.target
										);
										setDirty(true);
									}}
									name="bullhornClientId"
									label="Bullhorn Client Id"
									value={values.bullhornClientId.value}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										console.log("options:", options.target);
										setFieldValue(
											"bullhornClientSecret",
											options.target
										);
										setDirty(true);
									}}
									name="bullhornClientSecret"
									label="Bullhorn Client Secret"
									value={values.bullhornClientSecret.value}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										console.log("options:", options.target);
										setFieldValue(
											"bullhornUsername",
											options.target
										);
										setDirty(true);
									}}
									name="bullhornUsername"
									label="Bullhorn Username"
									value={values.bullhornUsername.value}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										console.log("options:", options.target);
										setFieldValue(
											"bullhornPassword",
											options.target
										);
										setDirty(true);
									}}
									name="bullhornPassword"
									label="Bullhorn Password"
									value={values.bullhornPassword.value}
								/>
							</>
						) : (
							<></>
						)}

						{values.crmIntegration.value === "Alto" ? (
							<Input
								toolTipText="Your Alto Agency Reference"
								onChange={(options) => {
									setFieldValue(
										"altoAgencyRef",
										options.target
									);
									setDirty(true);
								}}
								name="altoAgencyRef"
								label="Alto Agency Ref"
								value={values.altoAgencyRef.value}
							/>
						) : (
							<></>
						)}
						{values.crmIntegration.value === "Eploy" ? (
							<>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										const tenantValue =
											options.target.value;
										if (tenantValue.includes(".")) {
											setEployTenantError(
												"Enter only your Eploy tenant name (for example: company), not a full URL."
											);
										} else {
											setEployTenantError("");
										}
										setFieldValue(
											"eployTenant",
											options.target
										);
										setDirty(true);
									}}
									name="eployTenant"
									label="Eploy Tenant"
									value={values.eployTenant.value}
									error={eployTenantError}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										setFieldValue(
											"eployUserId",
											options.target
										);
										setDirty(true);
									}}
									name="eployUserId"
									label="Eploy User Id"
									value={values.eployUserId.value}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										setFieldValue(
											"eployClientId",
											options.target
										);
										setDirty(true);
									}}
									name="eployClientId"
									label="Eploy Client Id"
									value={values.eployClientId.value}
								/>
								<Input
									toolTipText="Leave blank to keep the existing secret"
									onChange={(options) => {
										setFieldValue(
											"eploySecret",
											options.target
										);
										setDirty(true);
									}}
									name="eploySecret"
									label={
										config.EployClientSecret
											? "Eploy Secret (stored — enter a new value to replace)"
											: "Eploy Secret"
									}
									value={values.eploySecret.value}
								/>
							</>
						) : (
							<></>
						)}
						{values.crmIntegration.value === "Vincere" ? (
							<div style={{ marginBottom: "50px" }}>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										setVincereClientID(
											options.target.value
										);
										setFieldValue(
											"vincereClientId",
											options.target
										);
										setDirty(true);
									}}
									name="vincereClientId"
									label="Vincere Client ID"
									value={values.vincereClientId.value}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										setVincereApiKey(options.target.value);
										setFieldValue(
											"vincereApiKey",
											options.target
										);
										setDirty(true);
									}}
									name="vincereApiKey"
									label="Vincere Api Key"
									value={values.vincereApiKey.value}
								/>
								<Input
									toolTipText="Explain here"
									onChange={(options) => {
										const tenantValue =
											options.target.value;
										setVincereTenant(tenantValue);
										if (tenantValue.includes(".")) {
											setVincereTenantError(
												"Enter only your Vincere tenant name (for example: company), not a full URL."
											);
										} else {
											setVincereTenantError("");
										}
										setFieldValue(
											"vincereTenant",
											options.target
										);
										setDirty(true);
									}}
									name="vincereTenant"
									label="Vincere Tenant"
									value={values.vincereTenant.value}
									error={vincereTenantError}
								/>
								<p style={{ marginBottom: "20px" }}>
									Remember to click 'Save' after you connect!
								</p>
								<Button
									style={{
										float: "left",
										display: "block",
										marginRight: "10px",
									}}
									primary
									buttonName="Connect"
									type="button"
									onClick={handleVincereOAuth}
									loading={loading}
									disabled={
										config.IsVincereConnected === true ||
										vincereApiKey === "" ||
										vincereClientId === "" ||
										vincereTenant === "" ||
										vincereTenant.includes(".")
									}
								/>
								<Button
									style={{ float: "left", display: "block" }}
									primary
									buttonName="Disconnect"
									type="submit"
									onClick={() => {
										handleVincereConnected(false);
									}}
									loading={loading}
									disabled={
										config.IsVincereConnected === false
									}
								/>
							</div>
						) : (
							<></>
						)}
						<JobAdderSettings global={false} rootStore={rootStore} values={values} setDirty={setDirty} config={config} />
						<Button
							style={{ float: "right" }}
							primary
							buttonName="Save"
							type="submit"
							loading={loading}
							disabled={dirty === false}
						/>
						<div style={{ clear: "both" }}></div>
					</form>
				)}
			</Formik>
			<Prompt
				when={dirty}
				message="You have unsaved changes, are you sure you want to leave?"
			/>
		</>
	);
};

export default EditInboxSettings;
